import { SimpleSelect } from '@/UIComponents2/Select/SimpleSelect'
import { carmackClick } from '@/utils'
import { cn } from '@/utlis/cn'
import { useEffect, useRef, useState } from 'react'

type Tab = {
    id: string
    label: string
}

type VerticalTabsProps = {
    tabs: Tab[]
    onTabChange: (tab: string) => void
    currentTab: string
    className?: string
    mobileSuffix?: string
    children?: React.ReactNode
}

export function VerticalTabs(props: VerticalTabsProps) {
    const [scrollPositions, setScrollPositions] = useState<Record<string, number>>({} as Record<string, number>)
    const tabContentRef = useRef<HTMLDivElement>(null)

    const saveScrollPosition = () => {
        setScrollPositions((prev) => ({
            ...prev,
            [props.currentTab]: tabContentRef.current?.scrollTop ?? 0,
        }))
    }

    useEffect(() => {
        // Restore scroll position for the new tab
        if (tabContentRef.current) {
            const pos = scrollPositions[props.currentTab] ?? 0
            tabContentRef.current.scrollTop = pos
        }
    }, [props.currentTab, scrollPositions])

    return (
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[min-content_1fr] gap-[10px] md:gap-[15px]">
            <>
                {/* Mobile: dropdown */}
                <div className="block md:hidden">
                    <SimpleSelect
                        value={props.currentTab}
                        onChange={(val) => props.onTabChange(val as any)}
                        items={props.tabs.map((tab) => ({
                            value: String(tab.id),
                            label: props.mobileSuffix ? `${tab.label} ${props.mobileSuffix}` : tab.label,
                        }))}
                        widthMatching
                    />
                </div>

                {/* Desktop: vertical list */}
                <div className={cn('hidden md:flex flex-col gap-[4px]', props.className)}>
                    {props.tabs.map((tab) => (
                        <Tab
                            key={String(tab.id)}
                            tab={tab}
                            selected={tab.id === props.currentTab}
                            onClick={() => {
                                saveScrollPosition()
                                props.onTabChange(tab.id)
                            }}
                        />
                    ))}
                </div>
            </>

            <div ref={tabContentRef} className="h-full overflow-y-auto pr-[10px] pb-8">
                {props.children}
            </div>
        </div>
    )
}

type TabProps = {
    tab: Tab
    selected: boolean
    onClick: () => void
    className?: string
}
function Tab<T>(props: TabProps) {
    const style =
        'py-[8px] min-w-[120px] flex items-center justify-start px-[15px] t-base-medium cursor-pointer whitespace-nowrap'
    const bg = 'rounded-[14px]'
    const selectedStyle = 'bg-[rgba(255,255,255,0.08)] border-muse-toplit shadow-glow'
    const unSelectedStyle = 'opacity-60 hover:opacity-100 border border-white/0'

    return (
        <button
            className={cn(style, bg, props.selected ? selectedStyle : unSelectedStyle, props.className)}
            onMouseDown={carmackClick(() => props.onClick())}
            onKeyDown={(ev) => ev.key === ' ' && props.onClick()}
        >
            {props.tab.label}
        </button>
    )
}
